import { SectionHeader } from "../shared/SectionHeader";
import { Card, CardContent } from "@/components/ui/card";
import { ToggleRow } from "../shared/ToggleRow";
import { useEmployeeViewSettings } from "@/hooks/useEmployeeViewSettings";
import { useToast } from "@/hooks/use-toast";

export function Permissions() {
  const { settings, saveSetting } = useEmployeeViewSettings();
  const { toast } = useToast();

  const persist = async (
    key: keyof typeof settings,
    value: boolean,
  ) => {
    try {
      await saveSetting(key, value);
    } catch (error) {
      toast({
        title: "Could not save permission",
        description: error instanceof Error ? error.message : "Try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <>
      <SectionHeader
        title="Permissions"
        description="Business-wide controls for what employees can see and do."
      />
      <p className="text-[12px] text-muted-foreground mb-4">
        Core roles still come from Team & Access. These toggles refine the employee workspace without changing who is an admin or employee.
      </p>
      <div className="space-y-0">
        <ToggleRow
          label="Employee can record sales"
          description="Shows Manual Entry and the record-sale action on stock items."
          checked={settings.allowEmployeeRecordSales}
          onSave={(value) => persist("allowEmployeeRecordSales", value)}
        />
        <ToggleRow
          label="Employee can view sales history"
          description="Shows the employee Sales Log page with their own recorded sales only."
          checked={settings.allowEmployeeViewSalesHistory}
          onSave={(value) => persist("allowEmployeeViewSalesHistory", value)}
        />
        <ToggleRow
          label="Employee can log expenses"
          description="Shows the Expenses page. Employees only see and manage their own expense entries."
          checked={settings.allowEmployeeRecordExpenses}
          onSave={(value) => persist("allowEmployeeRecordExpenses", value)}
        />
        <ToggleRow
          label="Employee can use Transport & Logistics"
          description="Shows purchase orders, deliveries, and stock receipt on Pro plans—same operations flow as admins for the business."
          checked={settings.allowEmployeeTransportLogistics}
          onSave={(value) => persist("allowEmployeeTransportLogistics", value)}
        />
        <ToggleRow
          label="Employee can add stock items"
          description="Allows new products to be added from the employee stock-check view."
          checked={settings.allowEmployeeAddInventoryItems}
          onSave={(value) => persist("allowEmployeeAddInventoryItems", value)}
        />
        <ToggleRow
          label="Employee can adjust stock counts"
          description="Allows stock count and low-threshold edits from the employee stock-check view."
          checked={settings.allowEmployeeAdjustInventory}
          onSave={(value) => persist("allowEmployeeAdjustInventory", value)}
        />
        <ToggleRow
          label="Employee can export inventory"
          description="Allows CSV export from the employee stock-check screen."
          checked={settings.allowEmployeeExportInventory}
          onSave={(value) => persist("allowEmployeeExportInventory", value)}
        />
        <ToggleRow
          label="Employee can delete own sales within 24 hours"
          description="Shows a delete action in My Activity for the employee's own recent sales only. Older entries stay read-only."
          checked={settings.allowEmployeeDeleteSale}
          onSave={(value) => persist("allowEmployeeDeleteSale", value)}
        />
      </div>
      <Card className="bg-card border-border mt-6">
        <CardContent className="pt-4">
          <p className="text-[12px] text-muted-foreground">
            Industry-standard default: employees can work inside their lane, but confidential business-wide expenses, profit views, and permission management stay with admins.
          </p>
        </CardContent>
      </Card>
    </>
  );
}
